import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MultiSelectChangeEventArgs } from '@syncfusion/ej2-dropdowns';
import { of, take } from 'rxjs';
import { LogService } from 'src/app/services/log.service';
import {
  IOrgCandidateProductListItem,
  IOrganizationProductListItem,
  OrganizationService,
  IBulkProductUpdateRequestVM,
  IBulkProductUpdatePagedResponse,
  IBulkProductUpdateMetadataResponse,
} from 'src/app/services/organization.service';
import { environment } from '../../../../../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { ExplicitConfirmationComponent } from '../../../../../components/explicit-confirmation/explicit-confirmation.component';
import { ProductFilterModel, TankFilterService } from '../../../../../services/tank-filter.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-organization-product-bulk-update',
  templateUrl: './organization-product-bulk-update.component.html',
  styleUrls: ['./organization-product-bulk-update.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationProductBulkUpdateComponent implements AfterViewInit, OnDestroy {
  @ViewChild('paginator') paginator: MatPaginator;

  @Input() orgId: number;

  isUpdateValuesButtonDisabled: boolean = true;
  tankCountNeedingUpdates: number = 0;
  isLoading = true; // Initial load
  isPageLoading = false; // Pagination/loading new pages
  isMetadataLoading = false; // Loading metadata (product dropdown)
  pendingMove: boolean = false;
  showSpinner: boolean = false;

  // Pagination properties
  currentPage = 0;
  pageSize = 25;
  totalCandidateProducts = 0;

  // Selection tracking across pages
  selectedProductsMap = new Map<string, boolean>();

  ddlFields: Object = { text: 'text', value: 'value' };

  displayedColumns = [
    { key: 'selected', type: 'bool', width: '80px' },
    { key: 'prodDesc', type: 'string', width: '200px' },
    { key: 'productSGU', type: 'string', width: '80px' },
    { key: 'userProductNumber', type: 'string', width: '80px' },
    { key: 'tankCount', type: 'int', align: 'center', width: '50px' },
    { key: 'recommendedProductDesc', type: 'string', width: '200px' },
  ];

  orgAvailableProductList: IOrganizationProductListItem[] = [];
  orgCandidateProductList: IOrgCandidateProductListItem[] = [];
  selectedProductId: any = null;
  ddlData: { [key: string]: Object }[] = [];

  // maps the appropriate column to fields property
  public productFields: Object = { text: 'prodDesc', value: 'productId' };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { orgId: number },
    private _mdr: MatDialogRef<OrganizationProductBulkUpdateComponent>,
    private _logger: LogService,
    private _os: OrganizationService,
    private tankFilterService: TankFilterService,
    private _cdr: ChangeDetectorRef,
    private route: Router,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<ExplicitConfirmationComponent, boolean>,
  ) {
    if (data) {
      this.orgId = data.orgId;
      this.initializePage();
    }
  }

  ngAfterViewInit(): void {
    // Subscribe to paginator events
    this.paginator.page.subscribe((event) => {
      this.onPageChange(event);
    });
  }

  ngOnDestroy(): void {
    // Reset loading states
    this.isPageLoading = false;
    this.isLoading = false;
  }

  onPageChange(event: any) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadCandidateProducts();
  }

  onCancelClick() {
    this._mdr.close();
  }

  //onMoveButtonClick() {
  //}

  onUpdateValuesButtonClicked() {
    // update values button was clicked

    // get the selected candidate products list from all pages using the selection map
    const selectedCandidateProducts: IOrgCandidateProductListItem[] = [];

    // Get all candidate products from all pages that are selected
    for (const [key, selected] of this.selectedProductsMap.entries()) {
      if (selected) {
        const [prodDesc, productSGU, userProductNumber] = key.split('|');
        // Find the product in current list or create a placeholder with the required fields
        const product = this.orgCandidateProductList.find(p =>
          p.prodDesc === prodDesc &&
          p.productSGU === parseFloat(productSGU) &&
          p.userProductNumber === userProductNumber
        );
        if (product) {
          selectedCandidateProducts.push(product);
        } else {
          // If not found in current page, we need to get it from the backend
          // For now, create a minimal object with required fields
          selectedCandidateProducts.push({
            selected: true,
            prodDesc,
            productSGU: parseFloat(productSGU),
            userProductNumber,
            tankCount: 0, // Will be populated by backend
            recommendedProductDesc: '',
            recommendedProductId: 0,
          } as IOrgCandidateProductListItem);
        }
      }
    }

    if (this.pendingMove === true) return;

    this.pendingMove = true;
    this._cdr.detectChanges();

    const request: IBulkProductUpdateRequestVM = {
      selectedProductId: this.selectedProductId,
      selectedProducts: selectedCandidateProducts,
      orgId: this.orgId,
    };
    this._os
      .showBulkUpdateConfirmationDialog(request)
      .afterClosed()
      .subscribe({
        next: (result) => {
          if (!result) return;

          //update the products
          this.showSpinner = true;
          this._os
            .bulkProductsUpdate(request)
            .pipe(take(1))
            .subscribe((response) => {
              if (!response || response.isSuccessful == false) {
                this.showErrorMessage(
                  `${this.translate.instant(
                    'OrganizationDetails.BulkUpdate.ErrorUpdatingProducts',
                  )}: ${response.ErrorMessage}`,
                  response,
                );
                return;
              }

              // show update summary
              this._os.showMoveStatusDialog(response.bulkProductsUpdated).afterClosed()
                .pipe(take(1)).subscribe((result) => {
                  this.dialogRef.close();
                  of(null)
                    .subscribe(() => {
                      if (!result) return;
                      //send user back to tank list
                      if (result?.sendToList === true) {
                        //set the Filter Model to product filter
                        const productDescFilter =
                          this.tankFilterService.createTankFilterModel(
                            ProductFilterModel
                          );
                        productDescFilter.values = response.bulkProductsUpdated.map(prod => prod.newProductValue)
                        this.tankFilterService.applyFilterModelToState(
                          productDescFilter,
                          this.constructor.name,
                          true,
                        );
                        this.route.navigate(['tank']);
                        return;
                      }
                    });
                });

              this.onRefreshClick();
            });
        }
      });
  }

  onRefreshClick() {
    this.currentPage = 0;
    this.isPageLoading = true;
    this.initializePage();
  }

  onCandidateSelectionChanged(index) {
    // a candidate selection checkbox changed
    const product = this.orgCandidateProductList[index];
    const key = this.getProductKey(product);

    // Store selection state in the map
    this.selectedProductsMap.set(key, product.selected);

    this.setIsUpdateValuesButtonDisabled();
    this.setProductOrderInDropDown();
  }
  
  getProductKey(product: IOrgCandidateProductListItem) {
    return `${product.prodDesc}|${product.productSGU}|${product.userProductNumber}`;
  }

  private setProductOrderInDropDown() {
    // order the drop down list based on the selected products

    // Get all selected products from the map
    const selectedProducts: IOrgCandidateProductListItem[] = [];
    for (const [key, selected] of this.selectedProductsMap.entries()) {
      if (selected) {
        const [prodDesc, productSGU, userProductNumber] = key.split('|');
        // Find the product in current list or create a placeholder
        const product = this.orgCandidateProductList.find(p =>
          p.prodDesc === prodDesc &&
          p.productSGU === parseFloat(productSGU) &&
          p.userProductNumber === userProductNumber
        );
        if (product) {
          selectedProducts.push(product);
        }
      }
    }

    //set the DropDown's selected items
    this.ddlData.forEach((item) => {
      item['productSelected'] = selectedProducts.some(
        (selectedProduct) => selectedProduct.recommendedProductId === (item['value'] as number)
      );
    });

    //sort the drop down list so that selected products are at the top
    this.ddlData = [...this.ddlData.sort((a, b) => {
      // if a is selected and b is not, a should come first
      if (a['productSelected'] && !b['productSelected']) return -1;
      // if b is selected and a is not, b should come first
      if (!a['productSelected'] && b['productSelected']) return 1;
      // if both are selected or both are not selected, sort by text
      return (a['text'] as string).localeCompare(b['text'] as string);
    })];
    this._cdr.detectChanges();
  }

  setIsUpdateValuesButtonDisabled() {
    // if any candidate product is selected, enable the update values button
    const hasSelectedProducts = Array.from(this.selectedProductsMap.values()).some(selected => selected);
    this.isUpdateValuesButtonDisabled =
      this.selectedProductId === null || !hasSelectedProducts;
  }

  initializePage() {
    this.pendingMove = false;
    this.isLoading = true;
    this.isMetadataLoading = true;
    this._cdr.markForCheck();

    // First load metadata (available products)
    this._os
      .getBulkProductUpdateMetadata(this.orgId)
      .pipe(take(1))
      .subscribe((metadata: IBulkProductUpdateMetadataResponse) => {
        this.orgAvailableProductList = metadata.orgAvailableProductList;
        this.mapDDLData();
        this.isMetadataLoading = false;
        this._cdr.markForCheck();

        // Then load paginated candidate products
        this.loadCandidateProducts();
      });
  }

  loadCandidateProducts() {
    this.isPageLoading = true;
    this._cdr.markForCheck();

    this._os
      .getBulkProductUpdateInfoPaged(this.orgId, {
        pageNumber: this.currentPage, // Convert to 1-based
        pageSize: this.pageSize,
      })
      .pipe(take(1))
      .subscribe((data: IBulkProductUpdatePagedResponse) => {
        this.orgCandidateProductList = data.orgCandidateProductList;
        this.totalCandidateProducts = data.totalCandidateProducts;
        this.tankCountNeedingUpdates = data.totalTankCount;

        // Restore selections from the map
        this.orgCandidateProductList.forEach(product => {
          const key = this.getProductKey(product);
          product.selected = this.selectedProductsMap.get(key) || false;
        });

        this.isLoading = false;
        this.isPageLoading = false;
        this.selectedProductId = null;
        this.isUpdateValuesButtonDisabled = true;

        this._cdr.markForCheck();
      });
  }

  mapDDLData() {
    this.ddlData = this.orgAvailableProductList.map((item) => {
      return {
        value: item.productId,
        text:
          item.prodDesc +
          ' : ' +
          item.productSGU +
          ' : ' +
          item.userProductNumber,
        productSelected: false,
      };
    });
  }

  onProductDropDownSelectChanged($event: any) {
    // an available product selection was changed
    this.selectedProductId = $event.value;
    this.setIsUpdateValuesButtonDisabled();
  }

  private showSuccessMessage(message: string) {
    this.snackBar.open(message, null, {
      duration: 5000,
      panelClass: ['snackbar-success'],
    });
  }

  private showErrorMessage(message: string, response: any) {
    this._logger.log('Location Save Error: ' + message, response);
    let duration = 5000;
    let errors = [];

    //process the error response.
    // Model may change depending on the error type.
    // Attempt to handle all possible error responses.
    if (response.error && response.error.constructor === Array) {
      errors = response.error;
    } else if (
      response.error &&
      response.error.errors &&
      response.error.errors.constructor === Array
    ) {
      errors = response.error.errors;
    }

    if (errors.length > 0) {
      const timestamperrorsExist = response.error.find(
        (error: any) => error.errorNo > 0 && error.errorNo < 4,
      );

      if (timestamperrorsExist) {
        duration = environment.snackbarDelayBeforeClose * 2; //increase the duration for timestamp errors to allow the user time to read the message
        message += `\n ${this.translate.instant('General.TimestampError')}`;
      } else {
        response.error.forEach((error: any) => {
          message += `\n ${this.translate.instant(
            'OrganizationDetails.Error.' + error.stringResourceKey,
          )}`;
        });
      }
    }

    this.snackBar.open(message, null, {
      duration: duration,
      panelClass: ['snackbar-failure'],
    });
  }
}
